import React, { useEffect, useCallback, useState } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupObject,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlAdd,
  gqlUpdate,
  gqlDelete,
  getCollectionById,
  gqlGetOrderByIdQuery,
  gqlGetWastereceptionpointsByCollectorId,
} from '../graphql';
import '../../../../styles/tab-styles.css';
import FormFields from './collection-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import { CollectionLineInput } from 'shared-pages/pages/collection-orders/graphql';
import {
  AFISCYPRUS_DEFAULT_MATERIALS,
  FOTOKIKLOSI_DEFAULT_MATERIALS,
  isRebatteryLiion,
  REBATTERYLIION_DEFAULT_MATERIALS,
} from 'shared-pages/common/constants';
import { Button, Spinner } from 'react-bootstrap';
import { DummyCollectorId } from 'shared-pages/shared/constants';
import { isAfisCyprus, isFotokiklosi } from 'shared-pages/common/constants';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';

import { apiBase } from 'shared-pages/common/api-helpfunc';

const _FILL_DEFAULT_WASTERECEPTIONPOINT = true;

// We'll switch from useLazyQuery to useQuery for the main fetch,
// using skip: isNew to avoid calling the server for new records.

// The route line / order data is still fetched by useLazyQuery if needed.
// This replicates the pattern used in the 'Production' code.

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const CollectionFormPage = ({ isModal = false, paramId, modalOnClose }: Props) => {
  const { handleGraphqlError } = useHandleError('collection');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();
  const effectiveCollectionOrderId = location?.state?.collectionOrderId;

  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  const [fetchWastereceptionpoints, { data: wastereceptionpointData }] = useLazyQuery(
    gqlGetWastereceptionpointsByCollectorId,
  );
  const [
    getCollectionOrderById,
    { data: collectionOrderData, loading: collectionOrderLoading, error: collectionOrderError },
  ] = useLazyQuery(gqlGetOrderByIdQuery);

  const {
    data: rawData,
    error,
    refetch,
  } = useQuery(getCollectionById, {
    variables: { id: idNum },
    skip: isNew,
    fetchPolicy: 'network-only',
  });

  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  useEffect(() => {
    // High-signal diagnostics for init-from-order flow
    console.log('[CollectionFormPage] mount/state snapshot', {
      isModal,
      urlId,
      paramId,
      id,
      idNum,
      isNew,
      locationState: location?.state,
      collectionOrderIdFromState: location?.state?.collectionOrderId,
      effectiveCollectionOrderId,
      collectionOrderIdType: typeof location?.state?.collectionOrderId,
      routeLineIdFromState: location?.state?.routeLineId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formShape = {
    id: YupNumeric,
    isActive: YupBoolean,
    code: YupString,
    seriesId: YupNumeric,
    codeNumber: YupNumeric,
    codePrefix: YupString,
    finDispNote: YupString,
    dateCollected: YupDate.required('Η Ημερομηνία ειναι υποχρεωτική'),
    colpointContactPerson: YupString,
    collectorId: YupNumeric.required('Ο Συλλέκτης είναι υποχρεωτικός'),
    collectionOrderId: YupNumeric,
    collectionOrders: Yup.array(),
    routeLineId: YupNumeric,
    driverId: YupNumeric,
    driver: YupObject.nullable(),
    vehicleId: YupNumeric,
    vehicle: YupObject.nullable(),
    collectionPointId: YupNumeric.required('Το Σημείο Συλλογής είναι υποχρεωτικό'),
    collectionpoint: Yup.object().shape({
      id: YupNumeric,
      seriesId: YupNumeric,
      code: YupString,
      codeNumber: YupNumeric,
      name: YupString,
      contactPerson: YupString,
      addressLine1: YupString,
      addressLine2: YupString,
      addressArea: YupString,
      postalCode: YupString,
      phone: YupString,
      mobile: YupString,
      profession: Yup.object()
        .shape({
          id: YupNumeric,
          name: YupString,
        })
        .nullable(),
    }),
    collector: Yup.object().shape({
      id: YupNumeric,
      name: YupString,
    }),
    wastereceptionpointId: YupNumeric.required('Το Σημείο Παραλαβής Αποβλήτων είναι υποχρεωτικό'),
    routeLine: Yup.object().nullable(),
    idocs: Yup.array()
      .of(
        Yup.object().shape({
          id: YupNumeric,
          identificationDocument: YupString,
        }),
      )
      .nullable(),
    collectionLines: Yup.array()
      .of(
        Yup.object().shape({
          id: YupNumeric,
          aa: YupNumeric,
          finDispNote: YupString,
          materialId: YupNumeric.required('Το Απόβλητο ειναι υποχρεωτικό'),
          jobtypeId: YupNumeric,
          qty: Yup.number()
            .moreThan(0, 'Η ποσότητα πρέπει να είναι μεγαλύτερη από 0')
            .required('Η ποσότητα ειναι υποχρεωτική'),
          netWeight: Yup.number()
            .moreThan(0, 'Το Βάρος πρέπει να είναι μεγαλύτερο από 0')
            .required('Το Βάρος ειναι υποχρεωτικό'),
          material: Yup.object()
            .shape({
              id: YupNumeric,
              name: YupString,
            })
            .nullable(),
        }),
      )
      .min(1, 'Πρέπει να προσθέσετε τουλάχιστον ένα Απόβλητο στην Συλλογή'),
    /*collectionOrders: Yup.array().of(
      Yup.object()
        .shape({
          id: YupNumeric.nullable(),
          code: YupString.nullable(),
        })
        .nullable(),
    ), */
  };
  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values: any) => Lo.pick(values, Lo.keys(formShape));

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);

  // Αρχικοποίηση απο κενό
  const initializeNewRecord = () => {
    let collectionLines = [];
    if (isFotokiklosi) {
      collectionLines.push(...FOTOKIKLOSI_DEFAULT_MATERIALS);
    } else if (isAfisCyprus) {
      collectionLines.push(AFISCYPRUS_DEFAULT_MATERIALS);
    } else if (isRebatteryLiion) {
      collectionLines.push(REBATTERYLIION_DEFAULT_MATERIALS);
    }
    setInitialNewRecData({
      dateCollected: new Date(),
      isActive: true,
      collectionLines,
      collectorId: appStore.isCollector ? appStore.associatedId : null,
    });
    console.log('[CollectionFormPage] initializeNewRecord', { collectionLines });
  };

  /**
      Αρχικοποίηση από Αίτημα
  */
  const initializeRecordFromOrder = useCallback(() => {
    if (!collectionOrderData) return;

    const order = collectionOrderData.getCollectionOrderById;
    // Το αίτημα μπορεί να μην υπάρχει πλέον (π.χ. stale cache μετά από goBack στο /collection/-1)
    if (!order) return;

    console.log('[CollectionFormPage] initializeRecordFromOrder - source order snapshot', {
      orderId: order?.id,
      orderCode: order?.code,
      orderCollectorId: order?.collectorId,
      orderCollectionPointId: order?.collectionpoint?.id,
      orderCollectionPoint: order?.collectionpoint,
      injectData: location?.state?.injectData,
      locationState: location?.state,
    });
    if (order.collectorId && order.collectorId !== DummyCollectorId) {
      fetchWastereceptionpoints({ variables: { collectorId: order.collectorId } });
    }

    // Τα Materials είναι ήδη ορισμένα στο initializeNewRecord
    // let collectionLines = [];
    // if (isFotokiklosi) {
    //   collectionLines.push(...FOTOKIKLOSI_DEFAULT_MATERIALS);
    // } else if (isAfisCyprus) {
    //   collectionLines.push(AFISCYPRUS_DEFAULT_MATERIALS);
    // }
    setInitialNewRecData((prev) => {
      const injectData = location?.state?.injectData;
      const effectiveInjectData = injectData;
      if (isRebatteryLiion && effectiveInjectData) {
        prev.wastereceptionpointId = effectiveInjectData.wastereceptionpointId;
        prev.collectionLines[0].netWeight = effectiveInjectData.netWeight;
        prev.collectionLines[0].qty = effectiveInjectData.qty;
      }

      const nextValues = {
        ...prev,
        isActive: true,
        dateCollected: new Date(),
        collectionOrders: [{ id: order.id, code: order.code }],
        collectorId: appStore.isCollector ? appStore.associatedId : order.collectorId,
        collectionPointId: order.collectionpoint.id,
        collectionpoint: { ...order.collectionpoint },
        collectionOrderId: effectiveCollectionOrderId,
        routeLineId: location?.state?.routeLineId,
        // collectionLines, // Είναι ήδη ορισμένα στο initializeNewRecord
      };
      console.log('[CollectionFormPage] initializeRecordFromOrder - merged nextValues', {
        collectionPointId: nextValues?.collectionPointId,
        collectionpoint: nextValues?.collectionpoint,
        wastereceptionpointId: nextValues?.wastereceptionpointId,
        collectorId: nextValues?.collectorId,
        collectionOrderId: nextValues?.collectionOrderId,
        routeLineId: nextValues?.routeLineId,
      });
      return nextValues;
    });
  }, [
    collectionOrderData,
    appStore.associatedId,
    appStore.isCollector,
    location.state,
    effectiveCollectionOrderId,
  ]);
  // this useEffect is used to initialize a collection coming from order page or route page
  // it will be called when the component mounts using the location.state object
  // and will set the initial values for the form fields
  useEffect(() => {
    if (!isNew || !effectiveCollectionOrderId) return;

    console.log('[CollectionFormPage] triggering getCollectionOrderById', {
      id: effectiveCollectionOrderId,
      typeOf: typeof effectiveCollectionOrderId,
    });
    getCollectionOrderById({ variables: { id: effectiveCollectionOrderId } });
  }, [isNew, effectiveCollectionOrderId, getCollectionOrderById]);

  useEffect(() => {
    if (!isNew) return;

    console.log('[CollectionFormPage] getCollectionOrderById status', {
      loading: collectionOrderLoading,
      hasData: !!collectionOrderData,
      error: collectionOrderError
        ? { message: collectionOrderError.message, name: collectionOrderError.name }
        : null,
    });
  }, [isNew, collectionOrderLoading, collectionOrderData, collectionOrderError]);

  // Αρχικοποίηση από Αντιγραφή (Copy)
  const initializeRecordFromCopy = useCallback(() => {
    const copyFrom = location?.state?.copyFrom;
    if (!copyFrom) return;

    // Copy all fields except id, code, etc.
    let newRec: any = {
      ...copyFrom,
      id: undefined,
      code: '',
      codePrefix: '',
      codeNumber: undefined,
      dateCollected: new Date(),
      isActive: true,
      finDispNote: '',
      collectionOrderId: undefined,
      collectionOrders: [],
    };
    // Remove ids from lines
    if (Array.isArray(newRec.collectionLines)) {
      newRec.collectionLines = newRec.collectionLines.map((line) => ({
        ...line,
        id: undefined,
      }));
    }
    setInitialNewRecData(newRec);
  }, [location]);

  useEffect(() => {
    if (isNew && location?.state?.copyFrom) {
      console.log('[CollectionFormPage] init path: copyFrom');
      initializeRecordFromCopy();
    } else if (isNew && effectiveCollectionOrderId && collectionOrderData?.getCollectionOrderById) {
      console.log('[CollectionFormPage] init path: fromOrder', {
        collectionOrderId: effectiveCollectionOrderId,
        routeLineId: location?.state?.routeLineId,
      });
      initializeNewRecord(); // Βάζει τα Materials
      initializeRecordFromOrder(); // Βάζει τα υπόλοιπα πεδία
    } else if (isNew && !effectiveCollectionOrderId) {
      console.log(
        '[CollectionFormPage] init path: empty new record (no collectionOrderId in state)',
      );
      initializeNewRecord();
    }
  }, [
    isNew,
    location?.state?.copyFrom,
    effectiveCollectionOrderId,
    collectionOrderData,
    initializeRecordFromOrder,
  ]);

  //UseEffect to fetch wastereceptionpoints by collectorId if its
  //
  useEffect(() => {
    if (
      wastereceptionpointData?.getWastereceptionpointsByCollectorId?.length > 0 &&
      wastereceptionpointData?.getWastereceptionpointsByCollectorId?.length <= 1 &&
      _FILL_DEFAULT_WASTERECEPTIONPOINT
    ) {
      setInitialNewRecData((prev) => ({
        ...prev,
        wastereceptionpointId: wastereceptionpointData.getWastereceptionpointsByCollectorId[0].id,
      }));
    }
  }, [wastereceptionpointData]);

  const handleSubmit = async (values: any, actions: any) => {
    actions.setSubmitting(false);
    let mutValues = pickProperties(values);
    delete mutValues.collectionpoint;
    delete mutValues.collector;
    delete mutValues.routeLine;
    delete mutValues.idocs;
    delete mutValues.collectionOrders;
    delete mutValues.vehicle;
    delete mutValues.driver;
    console.log('Saving record with VALUES', values);
    convertFieldToInt(mutValues, ['seriesId', 'codeNumber']);

    mutValues.collectionLines = values.collectionLines.map((line: any) => {
      const newLine = Lo.pick(line, Lo.keys(new CollectionLineInput()));
      newLine.aa = line.aa ? parseInt(line.aa) : undefined;
      newLine.jobtypeId = line.jobtypeId ? parseInt(line.jobtypeId) : null;
      delete newLine.material;
      delete newLine.jobtype;
      delete newLine.tempId;
      newLine.qty = parseInt(line.qty);
      newLine.netWeight = parseFloat(line.netWeight);
      return newLine;
    });

    const mutation = isNew ? addNewRecord : updateRecord;
    const variables = { data: { ...mutValues, id: idNum } };

    try {
      const { data } = await mutation({ variables });
      const rec = data[Object.keys(data)[0]];
      toastSuccess(`Επιτυχής ${isNew ? 'Προσθήκη' : 'Ενημέρωση'} Εγγραφής [${rec?.id}]`);
      refetch && refetch();
      if (isNew && rec) history.push(`/collection/${rec?.id}`);
    } catch (error) {
      handleGraphqlError(`mutation-${isNew ? 'add' : 'update'}`, error);
    }
  };

  const handleDelete = useCallback(async () => {
    const confirmed = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (!confirmed) return;

    try {
      await deleteRecord({ variables: { id: idNum } });
      toastSuccess('Επιτυχής Διαγραφή');
      if (isModal && modalOnClose) {
        modalOnClose(idNum);
      } else {
        // Μετά τη διαγραφή πηγαίνουμε στο grid — το goBack() επέστρεφε στο /collection/-1 και έσκαγε
        history.push(generatePath(getRouteByName('Collections')));
      }
    } catch (error) {
      handleGraphqlError('mutation-delete', error);
    }
  }, [deleteRecord, idNum, history, handleGraphqlError, isModal, modalOnClose]);

  const initialValues = React.useMemo(() => {
    if (isNew) {
      console.log('[CollectionFormPage] initialValues(useMemo) from initialNewRecData', {
        collectionPointId: initialNewRecData?.collectionPointId,
        collectionpoint: initialNewRecData?.collectionpoint,
        wastereceptionpointId: initialNewRecData?.wastereceptionpointId,
        collectorId: initialNewRecData?.collectorId,
      });
      return initialNewRecData;
    }

    if (data) {
      const processedData = pickProperties(data);

      // Ensure collectionLines have proper jobtype structure
      if (processedData.collectionLines) {
        processedData.collectionLines = processedData.collectionLines.map((line: any) => ({
          ...line,
          jobtype: line.jobtype || { id: line.jobtypeId || null, description: '' },
        }));
      }

      return processedData;
    }

    return undefined;
  }, [isNew, initialNewRecData, data]);

  useEffect(() => {
    if (!isNew || !initialNewRecData) return;
    console.log('[CollectionFormPage] initialNewRecData changed', {
      collectionPointId: initialNewRecData?.collectionPointId,
      collectionpoint: initialNewRecData?.collectionpoint,
      wastereceptionpointId: initialNewRecData?.wastereceptionpointId,
      collectorId: initialNewRecData?.collectorId,
      collectionOrders: initialNewRecData?.collectionOrders,
    });
  }, [isNew, initialNewRecData]);

  if (!initialValues) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  /**
   * Βεβαίωση Παραλαβής
   * @param id
   */
  function handleCollectionCertificateClick(id: any): void {
    const url = `${apiBase()}/pdf/waste-reception-certificate/${id}`;
    window.open(url, '_blank').focus();
  }
  /**
   * Δελτίο Αποστολής
   * @param id
   */
  function handleConsignmentNoteClick(id: any): void {
    const url = `${apiBase()}/pdf/consignment-note/${id}`;
    window.open(url, '_blank').focus();
  }

  /**
   *  Render
   */
  return (
    <FormCard
      title={
        isNew
          ? `${t('Δήλωση Νέας Συλλογής Αποβλήτων')}`
          : `${t('Συλλογή Αποβλήτων')}: ${data?.code}`
      }
      onSubmit={handleSubmit}
      onDelete={isNew ? undefined : handleDelete}
      onReturn={null}
      permitSubmit={false}
      permitDelete={!isNew && (appStore.isPlatformOwner || appStore.isCollector)}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      idx={idNum}
      injectFooterElements={
        !isModal
          ? (formikCtx) => {
              if (!isNew && !formikCtx.dirty && isFotokiklosi) {
                return (
                  <>
                    <Button
                      onClick={() => handleCollectionCertificateClick(id)}
                      style={{ marginRight: '6px' }}
                    >
                      Βεβαίωση Παραλαβής
                    </Button>
                    <Button
                      variant="danger"
                      style={{ marginRight: '6px' }}
                      onClick={() => handleConsignmentNoteClick(id)}
                    >
                      Δελτίο Αποστολής
                    </Button>
                  </>
                );
              } else return undefined;
            }
          : (formikCtx) => <ButtonCloseModal onClick={() => modalOnClose && modalOnClose(-1)} />
      }
    >
      <FormFields isNew={isNew} data={data} />
      {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
    </FormCard>
  );
};
